import { useNavigate } from 'react-router-dom'
import type { PrescriptionListItem } from '../../store/prescriptionListSlice'

interface PrescriptionListTableProps {
  prescriptions: PrescriptionListItem[]
}

const statusStyles: Record<string, string> = {
  Active: 'bg-accent-subtle-bg text-status-success',
  Completed: 'bg-[#e8e6e2] text-text-muted',
  Discontinued: 'bg-[#f5ecd8] text-status-warning',
  Expired: 'bg-[#f5dede] text-status-error',
}

function formatRxNumber(num: number): string {
  return `RX-${String(num).padStart(4, '0')}`
}

function formatDate(dateStr: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function PrescriptionListTable({ prescriptions }: PrescriptionListTableProps) {
  const navigate = useNavigate()

  if (prescriptions.length === 0) {
    return (
      <div data-testid="prescriptions-table" className="flex flex-col">
        <p className="text-[12px] text-text-muted py-8 text-center">No prescriptions found</p>
      </div>
    )
  }

  return (
    <div data-testid="prescriptions-table" className="flex flex-col">
      {/* Header row */}
      <div
        className="rx-table-header grid items-center h-9 text-[11px] font-medium text-text-muted uppercase tracking-wide"
        style={{
          gridTemplateColumns: '100px 1.2fr 1.2fr 100px 80px 90px 100px 110px',
        }}
      >
        <span className="px-2">Rx Number</span>
        <span className="px-2">Patient Name</span>
        <span className="px-2">Medication Name</span>
        <span className="px-2 max-md:hidden">Dosage</span>
        <span className="px-2 max-lg:hidden">Frequency</span>
        <span className="px-2 max-lg:hidden">Refills</span>
        <span className="px-2">Status</span>
        <span className="px-2 max-md:hidden">Prescribed Date</span>
      </div>

      {/* Data rows */}
      {prescriptions.map((rx) => (
        <div
          key={rx.id}
          data-testid={`prescription-row-${rx.id}`}
          onClick={() => navigate(`/prescriptions/${rx.id}`)}
          className="rx-table-row grid items-center h-[44px] text-[13px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
          style={{
            gridTemplateColumns: '100px 1.2fr 1.2fr 100px 80px 90px 100px 110px',
          }}
        >
          <span className="px-2 text-[13px] font-[450] text-text-muted" data-testid="prescription-rx-number">
            {formatRxNumber(rx.prescription_number)}
          </span>
          <span className="px-2 font-[500] truncate" data-testid="prescription-patient-name">
            {rx.patient_name}
          </span>
          <span className="px-2 truncate" data-testid="prescription-medication-name">
            {rx.medication_name}
          </span>
          <span className="px-2 text-[12px] text-text-secondary truncate max-md:hidden" data-testid="prescription-dosage">
            {rx.dosage}
          </span>
          <span className="px-2 text-[12px] text-text-secondary max-lg:hidden" data-testid="prescription-frequency">
            {rx.frequency}
          </span>
          <span className="px-2 max-lg:hidden" data-testid="prescription-remaining-refills">
            <span
              className={`text-[12px] ${
                rx.remaining_refills === 0 ? 'font-medium text-status-error' : 'text-text-secondary'
              }`}
            >
              {rx.remaining_refills}
            </span>
          </span>
          <span className="px-2" data-testid="prescription-status">
            <span
              className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${statusStyles[rx.status] || 'bg-[#e8e6e2] text-text-muted'}`}
            >
              {rx.status}
            </span>
          </span>
          <span className="px-2 text-[12px] text-text-muted max-md:hidden" data-testid="prescription-prescribed-date">
            {formatDate(rx.start_date)}
          </span>
        </div>
      ))}
    </div>
  )
}
